/* Rankings V11: one product list, published partner scores, separate owner evidence.
 * Keep the original model for legacy consumers. Explicitly opt into the partner
 * adapter by passing options.partners. Raw review and catalog feeds are not edited.
 */
(function (g) {
  'use strict';
  const base = g.LinsoulRankingsModel || (typeof require === 'function' ? require('./rb-model.js') : null);
  const partners = g.LinsoulPartnerReviews || (typeof require === 'function' ? require('./partner-reviews-model.js') : null);
  if (!base || !partners) throw Error('Load rankings and partner-review models before the unified adapter.');
  const validDate = (v, asOf) => {
    if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
    const parsed = new Date(v + 'T12:00:00Z');
    return Number.isFinite(parsed.valueOf()) && parsed.toISOString().slice(0,10) === v && (!asOf || v <= asOf);
  };
  function publicationDate(entries, asOf) {
    // Only the original article-publication field qualifies; checked/updated and
    // product published=true flags never become a publication date.
    const candidates = entries.filter(r => validDate(r.published, asOf)).slice().sort((a,b) =>
      b.published.localeCompare(a.published) || String(a.id).localeCompare(String(b.id)));
    const r = candidates[0];
    return r ? {date:r.published, sourceURL:r.sourceURL, publisher:r.publisher, reviewId:r.id,
      relationship:r.relationship || 'not-established'} : null;
  }
  function partnerExpert(feed, handle, preview, minimum) {
    const options = {preview, asOf:feed.snapshot, minimum};
    const a = partners.aggregate(feed.reviews || [], handle, options);
    const selected = new Set(a.eligible.map(r => r.id));
    const entries = a.cards.map(r => {
      const exclusions = partners.reasons(r, options).slice();
      if (!exclusions.length && !selected.has(r.id)) exclusions.push('Another eligible review represents this publication; one vote per publisher group.');
      return {...r, normalized:selected.has(r.id) ? partners.normalized(r) : null, exclusions,
        includedInAverage:selected.has(r.id)};
    }).sort((a,b) => String(b.published || '').localeCompare(String(a.published || '')) || a.publisher.localeCompare(b.publisher));
    const scored = entries.filter(r => r.includedInAverage);
    const reviewCount = entries.filter(r => r.sourceClass!=='brand-hosted' && ['review','multi-product review','paid collaboration review'].includes(r.contentType)).length;
    return {entries, scored, rejected:[], count:a.scoredPublishers, total:entries.length, reviewCount, coverageCount:entries.length-reviewCount,
      sources:a.sources, average:a.normalizedMean, normalizedScore:a.normalizedMean, stars:a.stars, display:partners.formatStars(a.stars),
      range:scored.length ? [Math.min(...scored.map(r=>r.normalized)),Math.max(...scored.map(r=>r.normalized))] : null,
      excludedCount:entries.length-scored.length,
      aggregation:'arithmetic-mean', checked:feed.snapshot, isPartner:true};
  }
  function create(catalog, expertFeed, ownerFeed, measurements, options = {}) {
    if (!options.partners) return base.create(catalog, expertFeed, ownerFeed, measurements, options);
    const model = base.create(catalog, expertFeed, ownerFeed, measurements, options);
    const feed = options.partners, preview = options.preview !== false;
    model.rows.forEach(r => {
      r.legacyExpert = r.expert;
      r.expert = partnerExpert(feed, r.product.handle, preview, 2);
      r.hasEvidence = r.expert.total > 0 || r.legacyExpert.total > 0 || Boolean(r.owner?.available);
      const refs = r.expert.entries.length ? r.expert.entries : r.legacyExpert.entries;
      r.publication = publicationDate(refs, feed.snapshot);
    });
    Object.assign(model.coverage, {
      evidence:model.rows.filter(r=>r.hasEvidence && r.variants.length).length,
      expertRanked:model.rows.filter(r=>r.expert.count>=2 && r.variants.length).length,
      ownerRanked:model.rows.filter(r=>r.owner?.available && r.owner.count>=10 && r.variants.length).length,
      partnerSnapshot:feed.snapshot, ownerSnapshot:ownerFeed.checked,
      partnerSources:(feed.reviews || []).filter(r=>partners.visible(r,{preview})).length
    });
    const oldSelect = model.select;
    model.select = function(input = {}) {
      const result = oldSelect(input);
      // Compare at the same two-decimal star precision that customers see.
      [...result.ranked,...result.limited].forEach(r => {
        r.roundedScore = r.score === null ? null : Number(partners.formatStars(
          result.lens === 'experts' ? partners.toStars(r.score) : r.score));
      });
      result.ranked.sort((a,b) => b.roundedScore-a.roundedScore || b.count-a.count || a.product.name.localeCompare(b.product.name));
      result.ranked.forEach((r,i) => { r.rank = i && result.ranked[i-1].roundedScore === r.roundedScore ? result.ranked[i-1].rank : i+1; });
      // Ranked positions remain governed by the existing evidence minimums.
      // Single-source scores and unavailable scores follow in the SAME list.
      result.limited.sort((a,b) => Number(b.score !== null)-Number(a.score !== null) ||
        (a.score !== null && b.score !== null ? b.roundedScore-a.roundedScore : 0) ||
        b.count-a.count || Number(b.hasEvidence)-Number(a.hasEvidence) || a.product.name.localeCompare(b.product.name));
      const all = [...result.ranked,...result.limited];
      const pageSize = 10;
      const pageCount = Math.max(1,Math.ceil(all.length / pageSize));
      const requested = Number(input.page);
      const page = Math.max(1,Math.min(pageCount,Number.isFinite(requested) ? Math.trunc(requested) : 1));
      return {...result, all, page, pageSize, pageCount, pageRows:all.slice((page-1)*pageSize,page*pageSize),
        from:all.length ? (page-1)*pageSize+1 : 0, to:Math.min(page*pageSize,all.length)};
    };
    return model;
  }
  const api = {...base, create, publicationDate, partnerExpert};
  g.LinsoulUnifiedRankingsModel = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
